import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSION_KEYS, hasPermission } from '../../data/userPermissions';
import { supabase } from '../../lib/supabase';

const RoleBasedHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, signOut: supabaseSignOut, user, loading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [factoryDirectoryAccess, setFactoryDirectoryAccess] = useState(false);
  // Team Access menu item: shown only for buyer accounts that are their
  // own owner (not a linked partner/employee of someone else's account).
  const [accountLink, setAccountLink] = useState(null);

  // Back-button gate: always re-push so authenticated users can't navigate back

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef?.current && !profileDropdownRef?.current?.contains(event?.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (loading) return;
    const loggedIn = !!user;
    if (!loggedIn) {
      setUserRole(null);
      setUserEmail('');
      return;
    }
    const role = localStorage.getItem('userRole');
    const email = user?.email || localStorage.getItem('userEmail') || '';
    setUserRole(role ? role?.toLowerCase()?.trim() : 'buyer');
    setUserEmail(email);
    if (user?.email && !localStorage.getItem('userEmail')) {
      localStorage.setItem('userEmail', user?.email);
    }
  }, [user, loading]);

  // Factory Directory: admin always has access (no query needed); other roles
  // depend on the internal_factory_directory secondary permission. Checked
  // once per login/role change, not on every render.
  useEffect(() => {
    if (!user?.id) {
      setFactoryDirectoryAccess(false);
      return;
    }
    if (userRole === 'admin') {
      setFactoryDirectoryAccess(true);
      return;
    }
    let cancelled = false;
    hasPermission(user?.id, PERMISSION_KEYS?.INTERNAL_FACTORY_DIRECTORY)?.then((enabled) => {
      if (!cancelled) setFactoryDirectoryAccess(!!enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, userRole]);

  useEffect(() => {
    if (!user?.id || userRole !== 'buyer') { setAccountLink(null); return; }
    let cancelled = false;
    supabase
      ?.from('business_account_links')
      ?.select('id')
      ?.eq('linked_user_id', user?.id)
      ?.eq('status', 'active')
      ?.maybeSingle()
      ?.then(({ data }) => {
        if (!cancelled) setAccountLink(data ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, userRole]);

  const getNavigationItems = () => {
    const baseItems = [
      { name: 'header.home', path: '/', icon: 'Home' }
    ];

    let items;
    switch (userRole) {
      case 'buyer':
        items = [
          ...baseItems,
          { name: 'header.marketplace', path: '/sugar-marketplace-dashboard', icon: 'Store' },
          { name: 'header.operations', path: '/buyer-operations-dashboard', icon: 'ClipboardList' },
          { name: 'header.trucks', path: '/truck-marketplace', icon: 'Truck' }
        ];
        break;

      case 'factory':
        items = [
          { name: 'Factory Dashboard', path: '/factory-quota-manager-dashboard', icon: 'Factory' },
          { name: 'Transporter Management', path: '/factory-transporter-management', icon: 'Truck' },
          { name: 'Quota Management', path: '/quota-allocation-pricing-center', icon: 'Package' },
          { name: 'Buyer View', path: '/sugar-marketplace-dashboard', icon: 'Store' }
        ];
        break;

      case 'factory_relations_officer':
        items = [
          ...baseItems,
          { name: 'Submit Rates', path: '/factory-rate-intelligence-center', icon: 'Zap' },
          { name: 'Send Email', path: '/fro-email-center', icon: 'Mail' },
          { name: 'My Submissions', path: '/my-submissions', icon: 'FileText' },
          { name: 'History', path: '/submission-history', icon: 'History' },
        ];
        break;

      case 'admin':
        items = [
          ...baseItems,
          { name: 'header.marketplace', path: '/sugar-marketplace-dashboard', icon: 'Store' },
          { name: 'header.trucks', path: '/truck-marketplace', icon: 'Truck' },
          { name: 'header.operations', path: '/buyer-operations-dashboard', icon: 'ClipboardList' },
          { name: 'header.fleet', path: '/transporter-dashboard', icon: 'Navigation' },
          { name: 'header.factory', path: '/factory-management-hub', icon: 'Factory' },
          { name: 'header.quota', path: '/sugar-quota-analysis', icon: 'PieChart' },
          { name: 'header.tracking', path: '/live-shipment-tracking', icon: 'MapPin' },
          { name: 'header.reports', path: '/reports-generation-center', icon: 'FileBarChart' },
          { name: 'header.news', path: '/news-and-market-prices', icon: 'Newspaper' },
          { name: 'header.admin', path: '/admin-control-panel', icon: 'Settings' }
        ];
        break;

      default:
        items = baseItems;
    }

    if (factoryDirectoryAccess) {
      items = [
        ...items,
        { name: 'Factory Directory', path: '/internal-factory-directory', icon: 'BookOpen' }
      ];
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    setShowLogoutConfirm(false);
    await supabaseSignOut();
    localStorage.removeItem('userRole');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    setIsMobileMenuOpen(false);
    navigate('/login-authentication');
  };

  const getHeaderTitle = () => {
    const role = (userRole || '')?.toLowerCase()?.trim();
    if (role === 'buyer') return 'Buyer Dashboard';
    if (role === 'admin') return 'SugarHathi Admin';
    if (role === 'quota_manager' || role === 'quota manager' || role === 'quotamanager') return 'Quota Manager';
    if (role === 'factory') return 'Factory Manager';
    return 'SugarHathi';
  };

  return (
    <>
    <header className={`sticky top-0 z-50 w-full border-b ${isMobileMenuOpen ? 'bg-white' : 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'}`}>
      <div className="role-header-container">
        <div className="role-header-logo">
          <div className="role-header-logo-icon cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
            <img 
              src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" 
              alt="SugarHathi logo"
              className="w-full h-full object-contain p-1"
            />
          </div>
          <span className="role-header-logo-text">{getHeaderTitle()}</span>
        </div>

        <nav className="hidden md:flex items-center space-x-1">
          {navigationItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <button
                key={item?.path}
                onClick={() => navigate(item?.path)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all
                  ${isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }
                `}
              >
                <Icon name={item?.icon} size={18} />
                {item?.name?.startsWith('header.') ? t(item?.name) : item?.name}
              </button>
            );
          })}
        </nav>

        <div className="role-header-actions flex-shrink-0">
          <LanguageSwitcher />
          {!!user && (userEmail || user?.email) && (
            <div className="hidden lg:block relative" ref={profileDropdownRef}>
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg border border-gray-200 hover:bg-gray-200 transition-all duration-200 max-w-[200px]"
                title={userEmail || user?.email}
              >
                <Icon name="User" size={14} />
                <span className="truncate max-w-[140px]">{userEmail || user?.email}</span>
                <Icon name="ChevronDown" size={12} className={`flex-shrink-0 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                  <button
                    onClick={() => { navigate('/profile'); setIsProfileDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e4a72] transition-colors duration-150"
                  >
                    <Icon name="UserCircle" size={16} />
                    <span>My Profile</span>
                  </button>
                  {userRole === 'buyer' && !accountLink && (
                    <button
                      onClick={() => { navigate('/team-access'); setIsProfileDropdownOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e4a72] transition-colors duration-150"
                    >
                      <Icon name="Users" size={16} />
                      <span>Team Access</span>
                    </button>
                  )}
                  <button
                    onClick={() => { navigate('/change-password'); setIsProfileDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e4a72] transition-colors duration-150"
                  >
                    <Icon name="KeyRound" size={16} />
                    <span>Change Password</span>
                  </button>
                  <button
                    onClick={() => { navigate('/notifications'); setIsProfileDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e4a72] transition-colors duration-150"
                  >
                    <Icon name="Bell" size={16} />
                    <span>Notification Settings</span>
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button
                    onClick={() => { setIsProfileDropdownOpen(false); setShowLogoutConfirm(true); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {!!user ? (
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              aria-label="Sign out"
            >
              <Icon name="LogOut" size={18} />
              <span>{t('common.signOut')}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login-authentication')}
              className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] hover:opacity-90 rounded-lg transition-all duration-200 shadow-sm"
              aria-label="Sign in"
            >
              <Icon name="LogIn" size={18} />
              <span>Sign In</span>
            </button>
          )}
          <button
            onClick={toggleMobileMenu}
            className="role-header-mobile-toggle"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[1001] lg:hidden"
            onClick={toggleMobileMenu}
            aria-hidden="true"
          />
          
          <div className="fixed top-0 left-0 bottom-0 w-80 bg-white z-[1002] lg:hidden shadow-2xl transition-transform duration-300 ease-in-out">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] cursor-pointer" onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}>
                  <img 
                    src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" 
                    alt="SugarHathi logo"
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <span className="text-xl font-semibold text-gray-900">{getHeaderTitle()}</span>
              </div>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors duration-200"
                aria-label="Close mobile menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* mobile drawer: signed-in email */}
            {!!user && (userEmail || user?.email) && (
              <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-400">Signed in as</p>
                <p className="text-sm font-medium text-gray-700 truncate">{userEmail || user?.email}</p>
              </div>
            )}

            <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-120px)] bg-white">
              {navigationItems?.map((item) => {
                const isItemActive = location?.pathname === item?.path;
                return (
                  <button
                    key={item?.path}
                    onClick={() => {
                      navigate(item?.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                      ${isItemActive
                        ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="flex-1 text-left">{item?.name?.startsWith('header.') ? t(item?.name) : item?.name}</span>
                  </button>
                );
              })}
              
              {isAuthenticated ? (
              <>
                <button
                  onClick={() => { navigate('/profile'); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200"
                >
                  <Icon name="UserCircle" size={20} />
                  <span className="flex-1 text-left">My Profile</span>
                </button>
                {userRole === 'buyer' && !accountLink && (
                  <button
                    onClick={() => { navigate('/team-access'); setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200"
                  >
                    <Icon name="Users" size={20} />
                    <span className="flex-1 text-left">Team Access</span>
                  </button>
                )}
                <button
                  onClick={() => { navigate('/change-password'); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200"
                >
                  <Icon name="KeyRound" size={20} />
                  <span className="flex-1 text-left">Change Password</span>
                </button>
                <button
                  onClick={() => { navigate('/notifications'); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200"
                >
                  <Icon name="Bell" size={20} />
                  <span className="flex-1 text-left">Notification Settings</span>
                </button>
                <div className="my-2 border-t border-gray-200" />
                <button
                  onClick={() => { setIsMobileMenuOpen(false); setShowLogoutConfirm(true); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-all duration-200"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="flex-1 text-left">Sign Out</span>
                </button>
              </>
              ) : (
              <button
                onClick={() => { navigate('/login-authentication'); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium mt-4 border-t border-gray-200 pt-4 text-[#1e4a72] hover:bg-blue-50 transition-all duration-200"
              >
                <Icon name="LogIn" size={20} />
                <span className="flex-1 text-left">Sign In</span>
              </button>
              )}
            </nav>
          </div>
        </>
      )}
    </header>

    {/* Logout Confirmation Popup */}
    {showLogoutConfirm && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Icon name="LogOut" size={20} className="text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Sign Out?</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            You will be logged out of your SugarHathi account. Any unsaved changes will be lost.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSignOut}
              className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
            >
              Yes, Sign Out
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default RoleBasedHeader;
